/**
 * 🧠 SMART BOOKING MANAGEMENT SERVICE
 * Advanced booking modification system with intelligent calculations,
 * automatic refunds, additional charges, and cancellation policy enforcement
 */

import { differenceInDays, differenceInHours, parseISO, isAfter, isBefore } from 'date-fns';

export interface BookingModification {
  bookingId: string;
  originalBooking: BookingDetails;
  newBooking: Partial<BookingDetails>;
  modificationReason: string;
  modifiedBy: string; // Admin/Support staff ID
  modifiedAt: string;
}

export interface BookingDetails {
  id: string;
  startDate: string;
  endDate: string;
  vehicleId: string;
  customerId: string;
  originalAmount: number;
  paidAmount: number;
  serviceType: 'self_drive' | 'with_driver' | 'chauffeur_driven';
  bookingType: 'instant' | 'request_to_book';
  status: 'confirmed' | 'active' | 'completed' | 'cancelled';
  createdAt: string;
  vehicleCategory: 'economy' | 'standard' | 'premium' | 'luxury';
  location: string;
  additionalServices?: string[];
  discounts?: number;
}

export interface PricingCalculation {
  basePrice: number;
  durationDays: number;
  serviceTypeSurcharge: number;
  locationSurcharge: number;
  additionalServicesFee: number;
  weekendSurcharge: number;
  holidaySurcharge: number;
  discounts: number;
  taxes: number;
  totalAmount: number;
  breakdown: {
    label: string;
    amount: number;
    description: string;
  }[];
}

export interface CancellationPolicy {
  isFreeCancellation: boolean;
  cancellationFee: number;
  cancellationFeePercentage: number;
  refundAmount: number;
  hoursUntilBooking: number;
  cancellationTier: 'free' | 'low_fee' | 'medium_fee' | 'high_fee' | 'no_refund';
  policyDetails: string;
}

export interface ModificationResult {
  canModify: boolean;
  pricingComparison: {
    original: PricingCalculation;
    new: PricingCalculation;
    difference: number;
  };
  refundRequired: boolean;
  additionalPaymentRequired: boolean;
  refundAmount: number;
  additionalAmount: number;
  modificationFee: number;
  actions: BookingAction[];
  notifications: CustomerNotification[];
}

export interface BookingAction {
  type: 'refund' | 'charge' | 'notify' | 'update' | 'cancel';
  amount?: number;
  description: string;
  requiresApproval: boolean;
  automated: boolean;
  executedAt?: string;
}

export interface CustomerNotification {
  type: 'sms' | 'email' | 'push' | 'in_app';
  title: string;
  message: string;
  data: any;
}

/**
 * Smart Booking Service - The brain of booking modifications
 */
class SmartBookingService {
  // Configurable settings that can be updated
  private settings = {
    modificationFee: 2500, // Default modification fee in Naira
    taxRate: 0.075, // 7.5% VAT
    minimumModificationHours: 2 // Minimum hours before booking to allow modification
  };

  private vehicleRates = {
    economy: { baseRate: 15000, weekendMultiplier: 1.2 },
    standard: { baseRate: 25000, weekendMultiplier: 1.25 },
    premium: { baseRate: 35000, weekendMultiplier: 1.3 },
    luxury: { baseRate: 50000, weekendMultiplier: 1.4 }
  };

  // Method to update configuration
  updateSettings(newSettings: Partial<typeof this.settings>) {
    this.settings = { ...this.settings, ...newSettings };
  }

  // Get current settings
  getSettings() {
    return { ...this.settings };
  }

  private serviceTypeMultipliers = {
    self_drive: 1.0,
    with_driver: 1.4,
    chauffeur_driven: 1.8
  };

  private locationMultipliers: { [key: string]: number } = {
    'Lagos': 1.0,
    'Abuja': 1.1,
    'Port Harcourt': 0.9,
    'Kano': 0.8,
    'Ibadan': 0.85
  };

  /**
   * 💰 SMART PRICING CALCULATOR
   * Calculates exact pricing based on all factors
   * If originalBooking provided, uses actual vehicle rate instead of hardcoded rates
   */
  calculatePricing(booking: Partial<BookingDetails>, originalBooking?: BookingDetails): PricingCalculation {
    const startDate = parseISO(booking.startDate!);
    const endDate = parseISO(booking.endDate!);
    const durationDays = Math.max(1, differenceInDays(endDate, startDate));
    
    
    // Get base rates - use actual vehicle rate if original booking provided
    let basePrice: number;
    let actualDailyRate: number;
    
    if (originalBooking) {
      // Calculate actual daily rate from original booking
      const originalDuration = Math.max(1, differenceInDays(parseISO(originalBooking.endDate), parseISO(originalBooking.startDate)));
      actualDailyRate = originalBooking.originalAmount / originalDuration;
      basePrice = actualDailyRate * durationDays;
      
    } else {
      // Fallback to hardcoded rates
      const vehicleRates = this.vehicleRates[booking.vehicleCategory || 'standard'];
      actualDailyRate = vehicleRates.baseRate;
      basePrice = actualDailyRate * durationDays;
      
    }
    
    let serviceTypeSurcharge = 0;
    let locationSurcharge = 0;
    let weekendSurcharge = 0;
    let additionalServicesFee = 0;
    let holidaySurcharge = 0;
    let discounts = 0;
    let taxes = 0;
    let totalAmount = 0;
    
    if (originalBooking) {
      // When we have original booking, use simplified calculation
      // Just apply the daily rate × new duration
      totalAmount = basePrice; // Already calculated as actualDailyRate × durationDays
      
    } else {
      // Use complex calculation for new bookings without original data
      const vehicleRates = this.vehicleRates[booking.vehicleCategory || 'standard'];
      
      // Service type surcharge
      const serviceMultiplier = this.serviceTypeMultipliers[booking.serviceType || 'self_drive'];
      serviceTypeSurcharge = basePrice * (serviceMultiplier - 1);
      
      // Location surcharge
      const locationMultiplier = this.locationMultipliers[booking.location || 'Lagos'] || 1.0;
      locationSurcharge = basePrice * (locationMultiplier - 1);
      
      // Weekend surcharge (Friday-Sunday)
      const currentDate = new Date(startDate);
      for (let i = 0; i < durationDays; i++) {
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0) { // Fri, Sat, Sun
          weekendSurcharge += vehicleRates.baseRate * (vehicleRates.weekendMultiplier - 1);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Additional services
      additionalServicesFee = (booking.additionalServices?.length || 0) * 5000;
      
      // Holiday surcharge (simplified - would integrate with holiday API)
      holidaySurcharge = 0;
      
      // Discounts
      discounts = booking.discounts || 0;
      
      // Calculate subtotal
      const subtotal = basePrice + serviceTypeSurcharge + locationSurcharge + 
                      weekendSurcharge + additionalServicesFee + holidaySurcharge - discounts;
      
      // Taxes (configurable VAT rate)
      taxes = subtotal * this.settings.taxRate;
      
      // Total amount
      totalAmount = subtotal + taxes;
    }

    const breakdown = originalBooking ? [
      { 
        label: 'Total Amount', 
        amount: totalAmount, 
        description: `${durationDays} days × ₦${actualDailyRate.toLocaleString()}/day (actual vehicle rate)` 
      }
    ] : [
      { label: 'Base Price', amount: basePrice, description: `${durationDays} days × ₦${actualDailyRate.toLocaleString()}` },
      { label: 'Service Type Fee', amount: serviceTypeSurcharge, description: `Surcharge for ${booking.serviceType}` },
      { label: 'Location Fee', amount: locationSurcharge, description: `Surcharge for ${booking.location}` },
      { label: 'Weekend Premium', amount: weekendSurcharge, description: 'Weekend and holiday premium' },
      { label: 'Additional Services', amount: additionalServicesFee, description: `${booking.additionalServices?.length || 0} additional services` },
      { label: 'Discounts', amount: -discounts, description: 'Applied discounts' },
      { label: 'VAT (7.5%)', amount: taxes, description: 'Nigerian VAT' }
    ].filter(item => item.amount !== 0);

    return {
      basePrice,
      durationDays,
      serviceTypeSurcharge,
      locationSurcharge,
      additionalServicesFee,
      weekendSurcharge,
      holidaySurcharge,
      discounts,
      taxes,
      totalAmount,
      breakdown
    };
  }

  /**
   * 📅 CANCELLATION POLICY ENGINE
   * Determines cancellation fees and refund amounts
   */
  calculateCancellationPolicy(booking: BookingDetails): CancellationPolicy {
    const startDate = parseISO(booking.startDate);
    const now = new Date();
    const hoursUntilBooking = differenceInHours(startDate, now);
    
    let cancellationTier: CancellationPolicy['cancellationTier'];
    let cancellationFeePercentage: number;
    let policyDetails: string;
    
    // Determine cancellation tier based on timing
    if (hoursUntilBooking >= 48) {
      cancellationTier = 'free';
      cancellationFeePercentage = 0;
      policyDetails = 'Free cancellation - Full refund available';
    } else if (hoursUntilBooking >= 24) {
      cancellationTier = 'low_fee';
      cancellationFeePercentage = 0.1; // 10%
      policyDetails = 'Low cancellation fee - 10% charge applies';
    } else if (hoursUntilBooking >= 6) {
      cancellationTier = 'medium_fee';
      cancellationFeePercentage = 0.25; // 25%
      policyDetails = 'Medium cancellation fee - 25% charge applies';
    } else if (hoursUntilBooking >= 1) {
      cancellationTier = 'high_fee';
      cancellationFeePercentage = 0.5; // 50%
      policyDetails = 'High cancellation fee - 50% charge applies';
    } else {
      cancellationTier = 'no_refund';
      cancellationFeePercentage = 1.0; // 100%
      policyDetails = 'No refund - Booking cannot be cancelled';
    }
    
    const cancellationFee = booking.paidAmount * cancellationFeePercentage;
    const refundAmount = booking.paidAmount - cancellationFee;
    
    return {
      isFreeCancellation: cancellationTier === 'free',
      cancellationFee,
      cancellationFeePercentage,
      refundAmount,
      hoursUntilBooking,
      cancellationTier,
      policyDetails
    };
  }

  /**
   * 📋 CREATE PRICING FROM EXISTING BOOKING
   * Uses actual booking amount instead of recalculating
   */
  createPricingFromBooking(booking: BookingDetails): PricingCalculation {
    const startDate = parseISO(booking.startDate);
    const endDate = parseISO(booking.endDate);
    const durationDays = Math.max(1, differenceInDays(endDate, startDate));
    
    // Use the actual amount from the booking, not recalculated
    const actualAmount = booking.originalAmount || booking.paidAmount;
    
    // Create a pricing breakdown that matches the actual amount
    const breakdown = [
      { 
        label: 'Original Booking Amount', 
        amount: actualAmount, 
        description: `Actual amount paid for ${durationDays} days`
      }
    ];

    return {
      basePrice: actualAmount * 0.85, // Estimate base price (85% of total)
      durationDays,
      serviceTypeSurcharge: actualAmount * 0.1, // Estimate surcharges
      locationSurcharge: 0,
      additionalServicesFee: 0,
      weekendSurcharge: actualAmount * 0.05,
      holidaySurcharge: 0,
      discounts: 0,
      taxes: actualAmount * this.settings.taxRate, // Use actual tax amount
      totalAmount: actualAmount, // Use the real amount!
      breakdown
    };
  }

  /**
   * 🔄 SMART BOOKING MODIFICATION ENGINE
   * Analyzes booking changes and determines required actions
   */
  async analyzeBookingModification(
    originalBooking: BookingDetails, 
    newBookingData: Partial<BookingDetails>
  ): Promise<ModificationResult> {
    
    // Create modified booking object
    const modifiedBooking = { ...originalBooking, ...newBookingData };
    
    // For original pricing, use the actual paid amount, not recalculated
    const originalPricing = this.createPricingFromBooking(originalBooking);
    // For new pricing, use the actual vehicle rate from original booking
    const newPricing = this.calculatePricing(modifiedBooking, originalBooking);
    const priceDifference = newPricing.totalAmount - originalPricing.totalAmount;
    
    
    // Determine if modification is allowed
    const canModify = this.canModifyBooking(originalBooking);
    
    // Calculate modification fee (configurable fee for changes)
    // Always apply modification fee if there are changes, regardless of canModify for fee calculation
    const hasChanges = Object.keys(newBookingData).length > 0;
    const modificationFee = hasChanges ? this.settings.modificationFee : 0;
    
    
    // Determine required actions
    const actions: BookingAction[] = [];
    const notifications: CustomerNotification[] = [];
    
    let refundRequired = false;
    let additionalPaymentRequired = false;
    let refundAmount = 0;
    let additionalAmount = 0;
    
    if (canModify) {
      // Add modification fee
      const totalDifference = priceDifference + modificationFee;
      
      if (totalDifference > 0) {
        // Customer needs to pay additional amount
        additionalPaymentRequired = true;
        additionalAmount = totalDifference;
        
        actions.push({
          type: 'charge',
          amount: additionalAmount,
          description: `Additional payment required: ₦${priceDifference.toLocaleString()} price difference + ₦${modificationFee.toLocaleString()} modification fee`,
          requiresApproval: false,
          automated: false
        });
        
        notifications.push({
          type: 'email',
          title: 'Booking Modification - Additional Payment Required',
          message: `Your booking modification requires an additional payment of ₦${additionalAmount.toLocaleString()}. Please complete the payment to confirm your changes.`,
          data: { bookingId: originalBooking.id, amount: additionalAmount }
        });
        
      } else if (totalDifference < 0) {
        // Customer gets a refund
        refundRequired = true;
        refundAmount = Math.abs(totalDifference);
        
        actions.push({
          type: 'refund',
          amount: refundAmount,
          description: `Refund: ₦${Math.abs(priceDifference).toLocaleString()} price reduction minus ₦${modificationFee.toLocaleString()} modification fee`,
          requiresApproval: false,
          automated: true
        });
        
        notifications.push({
          type: 'email',
          title: 'Booking Modification - Refund Processed',
          message: `Your booking has been modified and a refund of ₦${refundAmount.toLocaleString()} has been processed to your original payment method.`,
          data: { bookingId: originalBooking.id, refundAmount }
        });
      } else {
        // Only modification fee applies
        additionalPaymentRequired = true;
        additionalAmount = modificationFee;
        
        actions.push({
          type: 'charge',
          amount: modificationFee,
          description: `Modification fee: ₦${modificationFee.toLocaleString()}`,
          requiresApproval: false,
          automated: false
        });
      }
      
      // Add update action
      actions.push({
        type: 'update',
        description: 'Update booking details in system',
        requiresApproval: false,
        automated: true
      });
      
      // Add notification action
      actions.push({
        type: 'notify',
        description: 'Send modification confirmation to customer',
        requiresApproval: false,
        automated: true
      });
    }
    
    const result: ModificationResult = {
      canModify,
      pricingComparison: {
        original: originalPricing,
        new: newPricing,
        difference: priceDifference
      },
      refundRequired,
      additionalPaymentRequired,
      refundAmount,
      additionalAmount,
      modificationFee,
      actions,
      notifications
    };
    
    return result;
  }

  /**
   * ✅ BOOKING MODIFICATION VALIDATOR
   * Checks if booking can be modified based on policies
   */
  private canModifyBooking(booking: BookingDetails): boolean {
    const startDate = parseISO(booking.startDate);
    const now = new Date();
    const hoursUntilBooking = differenceInHours(startDate, now);
    
    
    // Can't modify if booking already started or completed
    if (['active', 'completed', 'cancelled'].includes(booking.status)) {
      return false;
    }
    
    // For future bookings (months away), always allow modification
    if (hoursUntilBooking > 24 * 30) { // More than 30 days away
      return true;
    }
    
    // Must be at least minimum hours before booking starts
    if (hoursUntilBooking < this.settings.minimumModificationHours) {
      return false;
    }
    
    return true;
  }

  /**
   * 💳 PROCESS ADDITIONAL PAYMENT
   * Handles charging additional amounts to customer
   */
  async processAdditionalPayment(
    bookingId: string, 
    amount: number, 
    reason: string,
    processedBy: string
  ): Promise<{ success: boolean; transactionId?: string; error?: string }> {
    try {
      
      // In real implementation, integrate with payment gateway
      // For now, simulate payment processing
      const mockTransactionId = `TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Simulate payment delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate 95% success rate
      const success = Math.random() > 0.05;
      
      if (success) {
        return {
          success: true,
          transactionId: mockTransactionId
        };
      } else {
        return {
          success: false,
          error: 'Payment processing failed. Please try again or use a different payment method.'
        };
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      return {
        success: false,
        error: 'Payment system error. Please contact support.'
      };
    }
  }

  /**
   * 💰 PROCESS REFUND
   * Handles automatic refunds to customers
   */
  async processRefund(
    bookingId: string, 
    amount: number, 
    reason: string,
    processedBy: string
  ): Promise<{ success: boolean; refundId?: string; error?: string }> {
    try {
      
      // In real implementation, integrate with payment gateway refund API
      // For now, simulate refund processing
      const mockRefundId = `REF_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Simulate refund delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simulate 98% success rate for refunds
      const success = Math.random() > 0.02;
      
      if (success) {
        return {
          success: true,
          refundId: mockRefundId
        };
      } else {
        return {
          success: false,
          error: 'Refund processing failed. Manual intervention required.'
        };
      }
    } catch (error) {
      console.error('Refund processing error:', error);
      return {
        success: false,
        error: 'Refund system error. Please contact technical support.'
      };
    }
  }

  /**
   * ❌ SMART CANCELLATION PROCESSOR
   * Handles booking cancellations with intelligent policy application
   */
  async processCancellation(
    booking: BookingDetails, 
    cancelledBy: string, 
    reason: string
  ): Promise<{
    success: boolean;
    cancellationPolicy: CancellationPolicy;
    refundId?: string;
    error?: string;
  }> {
    try {
      
      // Calculate cancellation policy
      const cancellationPolicy = this.calculateCancellationPolicy(booking);
      
      let refundResult = null;
      
      // Process refund if applicable
      if (cancellationPolicy.refundAmount > 0) {
        refundResult = await this.processRefund(
          booking.id,
          cancellationPolicy.refundAmount,
          `Booking cancellation: ${reason}`,
          cancelledBy
        );
        
        if (!refundResult.success) {
          return {
            success: false,
            cancellationPolicy,
            error: refundResult.error
          };
        }
      }
      
      
      return {
        success: true,
        cancellationPolicy,
        refundId: refundResult?.refundId
      };
      
    } catch (error) {
      console.error('Cancellation processing error:', error);
      return {
        success: false,
        cancellationPolicy: this.calculateCancellationPolicy(booking),
        error: 'Cancellation processing failed. Please try again.'
      };
    }
  }

  /**
   * 📊 GET BOOKING MODIFICATION PREVIEW
   * Shows what will happen before making changes
   */
  async getModificationPreview(
    originalBooking: BookingDetails,
    proposedChanges: Partial<BookingDetails>
  ): Promise<ModificationResult> {
    return this.analyzeBookingModification(originalBooking, proposedChanges);
  }

  /**
   * 📈 GENERATE MODIFICATION REPORT
   * Creates detailed report for customer support
   */
  generateModificationReport(
    originalBooking: BookingDetails,
    modification: ModificationResult,
    modifiedBy: string
  ): string {
    const report = `
🔄 BOOKING MODIFICATION REPORT
=====================================

Booking ID: ${originalBooking.id}
Modified By: ${modifiedBy}
Modification Date: ${new Date().toISOString()}

ORIGINAL BOOKING:
- Dates: ${originalBooking.startDate} to ${originalBooking.endDate}
- Amount: ₦${modification.pricingComparison.original.totalAmount.toLocaleString()}
- Duration: ${modification.pricingComparison.original.durationDays} days

NEW BOOKING:
- Dates: ${originalBooking.startDate} to ${originalBooking.endDate}
- Amount: ₦${modification.pricingComparison.new.totalAmount.toLocaleString()}
- Duration: ${modification.pricingComparison.new.durationDays} days

FINANCIAL IMPACT:
- Price Difference: ₦${modification.pricingComparison.difference.toLocaleString()}
- Modification Fee: ₦${modification.modificationFee.toLocaleString()}
- Refund Required: ${modification.refundRequired ? `₦${modification.refundAmount.toLocaleString()}` : 'No'}
- Additional Payment: ${modification.additionalPaymentRequired ? `₦${modification.additionalAmount.toLocaleString()}` : 'No'}

ACTIONS TAKEN:
${modification.actions.map(action => `- ${action.type.toUpperCase()}: ${action.description}`).join('\n')}

STATUS: ${modification.canModify ? 'PROCESSED' : 'REJECTED'}
=====================================
    `;
    
    return report;
  }
}

export const smartBookingService = new SmartBookingService();
export default smartBookingService;